/*
    This is the Design page, the DnD
    composition editor.
*/

import React, { Component } from 'react'
import classNames from 'classnames'
import { func, bool, string } from 'prop-types'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { visit, error, click, track } from 'utils/tracker'
import { compositionType, trackingRoomInfoType, productsDataType } from 'models/propTypes'
import { withTranslation, Trans } from 'react-i18next'
import { isUnsupportedBrowser } from 'utils/browser'
import {
    scenesToggle,
    openDesignLab as openDesignLabAction,
    showDesignSummary as showDesignSummaryAction,
} from 'models/ui/actions'

import {
    getScenesOpen,
    getCompositionsOpen,
    getShouldPresentItemSelection,
    getShowMagicEraserTutorial,
    getShowDesignSummary,
} from 'models/ui/selectors'
import {
    getSelectedComposition,
    getSelectedCompositionTrackingRoomInfo,
    getSelectedCompositionRenderedItemsTotalPrice,
    getSelectedCompositionShoppableProductData,
    getIsSaving,
} from 'models/compositions/selectors'
import {
    saveSelectedComposition as saveSelectedCompositionAction,
    saveCompositionBeforeSharing as saveAndShareAction,
    renameComposition as renameCompositionAction,
} from 'models/compositions/actions'
import { getIsAdmin, getCanBeAdmin, getIsLoggedIn } from 'models/user/selectors'
import { getIsLowPerformance } from 'models/renderer/selectors'

import PreviewContainer from 'containers/PreviewContainer'
import SceneSelectionContainer from 'containers/SceneSelectionContainer'
import CompositionSelectionContainer from 'containers/CompositionSelectionContainer'
import SidepanelContainer from 'containers/designSidepanel/SidepanelContainer'
import DebugHotkeysContainer from 'containers/admin/DebugHotkeysContainer'
import SharePanelContainer from 'containers/designSidepanel/SharePanelContainer'
import DesignModeSelectorContainer from 'containers/design/DesignModeSelectorContainer'
import MagicEraserTutorialContainer from 'containers/design/MagicEraserTutorialContainer'
import PreviewHeaderContainer from 'containers/PreviewHeaderContainer'
import OpenCompositionModalContainer from 'containers/OpenCompositionModalContainer'
import Icon from 'components/uiElements/Icon'
import Tooltip from 'components/Tooltip'
import LowPerformanceModal from 'components/LowPerformanceModal'
import LoginPromptModalContainer from 'containers/accountAccess/LoginPromptModalContainer'
import DesignSummaryModalContainer from 'containers/DesignSummaryModalContainer'
import arrowRight from '@ingka/ssr-icon/paths/arrow-right'
import Button from '@ingka/button'
import DesignSummarySavePopup from 'components/DesignSummarySavePopup'
import AppDescriptionForScreenReader from 'components/AppDescriptionForScreenReader'
import SugarcubeContext from 'apps/SugarcubeContext'
import KioskShare from 'components/KioskShare'
import KioskIdlePopup from 'components/KioskIdlePopup'

require('./DesignPage.scss')

class DesignPage extends Component {
    constructor(props) {
        super(props)
        this.updateContentHeight = this.updateContentHeight.bind(this)
        this.contentContainerRef = React.createRef()
        this.setShowTryNewRoomModal = this.setShowTryNewRoomModal.bind(this)
        this.onTryNewRoomClick = this.onTryNewRoomClick.bind(this)
        this.setShowSavePopup = this.setShowSavePopup.bind(this)
        this.closeModalAndPopup = this.closeModalAndPopup.bind(this)
        this.setShowShare = this.setShowShare.bind(this)
        this.onSaveShareClick = this.onSaveShareClick.bind(this)

        this.state = {
            contentHeight: null,
            showTryNewRoomModal: false,
            showSavePopup: false,
            showShare: false,
        }
    }

    componentDidMount() {
        visit('DecorateRoom')
        if (isUnsupportedBrowser()) {
            error('UnsupportedBrowser')
        }
        window.addEventListener('resize', this.updateContentHeight)
        this.updateContentHeight()
    }

    componentDidUpdate(prevProps) {
        const { composition, roomInfo } = this.props
        const { composition: prevComposition } = prevProps

        if (
            composition &&
            composition.isMissingItems &&
            // If there's no comparison or the value has changed
            (!prevComposition || composition.isMissingItems !== prevComposition.isMissingItems)
        ) {
            track('DecorateRoom.Design.MissingProductsWarning', roomInfo)
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateContentHeight)
    }

    onTryNewRoomClick() {
        const { roomInfo } = this.props
        click('DecorateRoom.TryNewRoomCTA', roomInfo)
        this.setShowTryNewRoomModal(true)
    }

    onSaveShareClick() {
        const { saveAndShare, renameComposition, composition } = this.props
        if (composition) {
            if (this.context.isKiosk) {
                renameComposition(composition.id, `My H22 Design`)
            }
            saveAndShare()
            this.setShowShare(true)
        }
    }

    setShowShare(isShown) {
        this.setState({ showShare: isShown })
    }

    setShowSavePopup(isShown) {
        this.setState({ showSavePopup: isShown })
    }

    setShowTryNewRoomModal(isModalOpen) {
        this.setState({ showTryNewRoomModal: isModalOpen })
    }

    closeModalAndPopup() {
        const { showDesignSummary } = this.props
        this.setShowSavePopup(false)
        showDesignSummary(false)
    }

    updateContentHeight() {
        if (this.contentContainerRef.current) {
            const containerRect = this.contentContainerRef.current.getBoundingClientRect()
            this.setState({ contentHeight: containerRect.height })
        }
    }

    render() {
        const {
            scenesOpen,
            compositionsOpen,
            composition,
            isAdmin,
            canBeAdmin,
            isLowPerformance,
            t,
            showMagicEraserTutorial,
            totalPrice,
            saveSelectedComposition,
            openDesignLab,
            addedToRoomItems,
            isSaving,
            isDesignSummaryVisible,
            showDesignSummary,
            isLoggedIn,
        } = this.props

        const missingItemsWarning = composition ? composition.isMissingItems : false
        const unsupportedBrowserWarning = isUnsupportedBrowser()
        const showHeader = composition && composition.canMagicErase
        const { showTryNewRoomModal, showSavePopup, showShare } = this.state
        const hasProductsInDesign = !!addedToRoomItems.length

        const { ikeaNav, isKiosk, isMobile } = this.context
        const showSummaryButton = !isMobile && !isKiosk
        const showPreviewHeader = !isMobile && !isKiosk
        const showSaveShareButton = isKiosk && isLoggedIn
        const showHiddenLoginButton = isKiosk && !isLoggedIn

        if (isLowPerformance) {
            return <LowPerformanceModal />
        }

        return (
            <div id="DesignPage">
                <AppDescriptionForScreenReader />
                {isKiosk && <KioskIdlePopup />}
                {isAdmin || canBeAdmin ? <DebugHotkeysContainer /> : null}
                <div
                    className={classNames('contentContainer', {
                        isModalOpen: showTryNewRoomModal || isDesignSummaryVisible,
                    })}
                >
                    <div className="infoHeader">
                        {isLowPerformance && (
                            <div className="lowPerformanceWarning">
                                <Icon code="info_circle_invert" svg /> {t('common.low performance mode')}.
                                <a
                                    /* eslint-disable-next-line max-len */
                                    href="//support.mysugarcube.com/hc/en-us/articles/360052559294-Why-is-my-design-experience-running-in-Low-performance-mode-"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('common.learn more')}
                                </a>
                            </div>
                        )}
                    </div>
                    {showHeader && (
                        <div className={classNames('designHeader', { scenesOpen, compositionsOpen })}>
                            <div className="modeContainer">
                                {composition && composition.canMagicErase && <DesignModeSelectorContainer />}
                            </div>
                            <div>
                                <Tooltip />
                                <button
                                    onClick={this.props.toggleScenes}
                                    className="changeRoom"
                                    data-tip={t('tooltip.choose a different room')}
                                >
                                    {t('design.change room')}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className={classNames('missingItemsWarning', { visible: missingItemsWarning })}>
                        {t('common.items missing')}
                    </div>
                    <div className={classNames('unsupportedBrowserWarning', { visible: unsupportedBrowserWarning })}>
                        <Trans i18nKey="common.unsupportedBrowserWarning">
                            Your browser is not officially supported at this time. You may be see unexpected behavior
                            and find that some features do not work as expected.
                            <br />
                            Please use Mozilla Firefox or Google Chrome for a more reliable experience.
                        </Trans>
                    </div>
                    <SidepanelContainer />
                    <div
                        className={classNames('previewContainer', {
                            isModalOpen: showTryNewRoomModal || isDesignSummaryVisible,
                            isKiosk,
                        })}
                        ref={this.contentContainerRef}
                    >
                        {showMagicEraserTutorial && (
                            <div className="overlay">
                                <MagicEraserTutorialContainer />
                            </div>
                        )}
                        {scenesOpen ? <SceneSelectionContainer /> : null}
                        {compositionsOpen ? <CompositionSelectionContainer /> : null}
                        <div className="content">
                            {isUnsupportedBrowser() ? (
                                <div className="unsupportedContent">
                                    <Icon code="feedbackSad" />
                                </div>
                            ) : (
                                <div>
                                    {showPreviewHeader && (
                                        <PreviewHeaderContainer openTryNewRoomModal={this.onTryNewRoomClick} />
                                    )}
                                    <PreviewContainer
                                        maxHeight={this.state.contentHeight}
                                        setShowDesignSummary={() => showDesignSummary(true)}
                                        closeMagicWindow={null}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <SharePanelContainer />
                    {showTryNewRoomModal && (
                        <OpenCompositionModalContainer
                            closeTryNewRoomModal={() => this.setShowTryNewRoomModal(false)}
                        />
                    )}
                    {showSummaryButton && (
                        <div className="footer">
                            <div className="totalPrice">{totalPrice}</div>
                            <Button
                                onClick={() => showDesignSummary(true)}
                                text={t('design.summary')}
                                ssrIcon={arrowRight}
                                type="primary"
                                className="summaryButton"
                                iconPosition="trailing"
                                disabled={!hasProductsInDesign}
                            />
                        </div>
                    )}
                    {showSaveShareButton && (
                        <div className="footer">
                            <Button
                                onClick={this.onSaveShareClick}
                                text={t('kiosk.save & share')}
                                type="primary"
                                className="shareButton"
                            />
                        </div>
                    )}
                    {showHiddenLoginButton && (
                        <div className="footer">
                            <Link to="/login">
                                <button className="hiddenLoginButton" />
                            </Link>
                        </div>
                    )}
                    {isKiosk && showShare && (
                        <KioskShare closeModal={() => this.setShowShare(false)} uuid={composition.uuid} />
                    )}
                    {isDesignSummaryVisible && (
                        <DesignSummaryModalContainer
                            closeModal={() => showDesignSummary(false)}
                            openExitPopup={() => {
                                this.setShowSavePopup(true)
                            }}
                        />
                    )}
                    {showSavePopup && (
                        <DesignSummarySavePopup
                            isVisible={showSavePopup}
                            onSave={saveSelectedComposition}
                            closeModal={this.closeModalAndPopup}
                            ikeaNav={ikeaNav}
                            openDesignLab={openDesignLab}
                            isSaving={isSaving}
                        />
                    )}
                    <LoginPromptModalContainer />
                </div>
            </div>
        )
    }
}

DesignPage.contextType = SugarcubeContext
DesignPage.defaultProps = {
    composition: null,
    roomInfo: null,
}

DesignPage.propTypes = {
    toggleScenes: func.isRequired,
    scenesOpen: bool.isRequired,
    compositionsOpen: bool.isRequired,
    composition: compositionType,
    isAdmin: bool.isRequired,
    canBeAdmin: bool.isRequired,
    isLowPerformance: bool.isRequired,
    t: func.isRequired,
    showMagicEraserTutorial: bool.isRequired,
    roomInfo: trackingRoomInfoType,
    totalPrice: string.isRequired,
    saveSelectedComposition: func.isRequired,
    saveAndShare: func.isRequired,
    openDesignLab: func.isRequired,
    addedToRoomItems: productsDataType.isRequired,
    isSaving: bool.isRequired,
    isDesignSummaryVisible: bool.isRequired,
    showDesignSummary: func.isRequired,
    isLoggedIn: bool.isRequired,
    renameComposition: func.isRequired,
}

const mapStateToProps = (state) => ({
    scenesOpen: getScenesOpen(state),
    compositionsOpen: getCompositionsOpen(state),
    composition: getSelectedComposition(state),
    itemsPresenting: getShouldPresentItemSelection(state),
    isAdmin: getIsAdmin(state),
    canBeAdmin: getCanBeAdmin(state),
    isLowPerformance: getIsLowPerformance(state),
    showMagicEraserTutorial: getShowMagicEraserTutorial(state),
    roomInfo: getSelectedCompositionTrackingRoomInfo(state),
    totalPrice: getSelectedCompositionRenderedItemsTotalPrice(state),
    addedToRoomItems: getSelectedCompositionShoppableProductData(state),
    isSaving: getIsSaving(state),
    isDesignSummaryVisible: getShowDesignSummary(state),
    isLoggedIn: getIsLoggedIn(state),
})

const mapDispatchToProps = {
    toggleScenes: scenesToggle,
    saveSelectedComposition: saveSelectedCompositionAction,
    saveAndShare: saveAndShareAction,
    openDesignLab: openDesignLabAction,
    showDesignSummary: showDesignSummaryAction,
    renameComposition: renameCompositionAction,
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(DesignPage))
